/**
 * Client Model B self-signup bridge — `POST /v1/auth/self-join` (2026-06-22).
 *
 * The second half of firm-branded self-signup. A client who signed up from a
 * firm's branded link confirms their email, lands on `/auth/callback` with a
 * real Supabase account but NO tenant_id yet, and this hook attaches them to the
 * firm tenant the firm opted into (`{ firmSlug }`). The backend (on the
 * tenant-onboarding FA) verifies the firm's `openSignup.enabled` opt-in, links /
 * creates a `role:'user'` client UserDoc in the FIRM tenant, and stamps
 * Supabase `app_metadata = { tenant_id, role:'user' }`.
 *
 * After a 200 the SPA `refreshSession()`s (so the JWT carries the new tenant_id)
 * and routes the client into the normal document-first individual onboarding —
 * their TRUST is minted later, at confirm, inside the firm tenant.
 *
 * ## Errors the call-site must handle
 *   - 409 ALREADY_IN_TENANT — the account already belongs to a tenant. Treat as
 *     "already attached → just proceed" (idempotent; never an error to the user).
 *   - 403 SELF_SIGNUP_DISABLED / 403 NOT_A_FIRM — the firm hasn't opted in (or is
 *     not a firm tenant). Surface honestly.
 *   - 404 FIRM_NOT_FOUND — unknown slug. Surface honestly.
 *   - 400 NO_VERIFIED_EMAIL — the session has no verified email. Surface honestly.
 *   - 401 — bad/absent token (→ sign in).
 *   - 429 — rate limited.
 *
 * `noticeFlags.supabaseAdminSkipped` mirrors onboard/accept-invite: on a dev FA
 * with no service-role key the Cosmos link landed but app_metadata wasn't
 * stamped, so the caller must re-auth before protected calls see the tenant.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';

export interface SelfJoinFirmRequest {
  /** The firm's tenant slug (resolved from `?firm=` or the subdomain). */
  firmSlug: string;
}

export interface SelfJoinedProfile {
  userId: string;
  tenantId: string;
  role: 'user';
  displayName: string;
  /** WHO PAYS — echoed from the firm's opt-in. The individual onboarding wrapper
   *  reads this to show or skip the paid subscription step. */
  billingOwner: 'firm' | 'client';
}

export interface SelfJoinFirmResponse {
  joined: true;
  /** False when the caller was already attached to this firm (double-click /
   *  retry) — distinguishes "just joined" from "already in". */
  firstJoin: boolean;
  firmSlug: string;
  profile: SelfJoinedProfile;
  noticeFlags?: { supabaseAdminSkipped?: boolean };
  correlationId: string;
}

/**
 * Attach the just-confirmed client to a firm tenant by slug.
 *
 * Invalidates the tenant/users/trusts caches on success so the freshly-attached
 * session re-reads its (now tenant-scoped) data — matching `useOnboard`.
 */
export function useSelfJoinFirm() {
  const qc = useQueryClient();
  return useMutation<SelfJoinFirmResponse, ApiError, SelfJoinFirmRequest>({
    mutationFn: (body) =>
      api<SelfJoinFirmResponse>('/api/v1/auth/self-join', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenants'] });
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['trusts'] });
    },
  });
}
